import { useEffect, useMemo } from 'react';
import { assignablePermissionTypes } from '@common/constants/assignablePermissions';
import { usePrevious } from '@client-shared/hooks/usePrevious';
import { useBatchReportDetails as batchReportDetailsApi } from '../../shared/queries/batchReports';
import { BatchReportParameters } from '../../shared/types/batchReports';
import type { BatchReportDefaultParams } from '@common/types/batchReports';
import { SftpSchedule } from '@common/types/sftpSchedule';
import { parseJsonStringOrFallback } from '@common/utils/jsonUtils';
import { generateBatchReportParameters } from './helpers';
import { ExportFormat } from './types';

const ARCHIVE_ADMIN = assignablePermissionTypes.ArchiveAdmin;

export const useBatchReportDetails = (selectedBatchReportId: number) => {
  const previousSelectedBatchReportId = usePrevious(selectedBatchReportId);

  const {
    data: batchReportDetails,
    isFetching: isFetchingReportDetails,
    refetch: refetchBatchReportDetails,
  } = batchReportDetailsApi(selectedBatchReportId);

  const defaultParams = batchReportDetails?.defaultParams || '';

  const parsedDefaultParams = useMemo(
    () => parseJsonStringOrFallback<BatchReportDefaultParams>(defaultParams, {}),
    [defaultParams]
  );

  const initialBatchReportParameters = useMemo<BatchReportParameters>(() => {
    const defaultReportvalues = parsedDefaultParams.reports || {};
    const reports = batchReportDetails?.groupItem || [];
    return generateBatchReportParameters(reports, defaultReportvalues);
  }, [batchReportDetails?.groupItem, parsedDefaultParams]);

  const savedExportFormatValue = parsedDefaultParams.exportFormat;
  const savedExportFormat: ExportFormat = savedExportFormatValue === 'csv' || savedExportFormatValue === 'xlsx'
    ? savedExportFormatValue
    : 'xlsx';

  useEffect(() => {
    if (!previousSelectedBatchReportId || !selectedBatchReportId) {
      return;
    }
    if (previousSelectedBatchReportId !== selectedBatchReportId) {
      refetchBatchReportDetails();
    }
  }, [selectedBatchReportId, previousSelectedBatchReportId, refetchBatchReportDetails]);

  const sftpSchedule: SftpSchedule | null = parsedDefaultParams.sftpSchedule ?? null;

  return {
    archivalFlag: batchReportDetails?.archivalFlag || false,
    sftpDeliveryFlag: batchReportDetails?.sftpDeliveryFlag || false,
    batchReportDetails,
    initialBatchReportParameters,
    isAllowedToEdit: !!batchReportDetails?.privileges?.find(item => item === ARCHIVE_ADMIN),
    isFetchingReportDetails,
    reports: batchReportDetails?.groupItem || [],
    refetchBatchReportDetails,
    role: batchReportDetails?.role || '',
    savedExportFormat,
    sftpSchedule,
  };
};
